import React from "react";
import BuildingInformation from "./features/BuildingInformation";
import SettingsFooter from "./features/SettingsFooter";
import Modal from "components/Modal";
import { useTranslation } from "react-i18next";
import { BuildingCalculationEngine, PlaceDetailsOUT } from "api-client";
import clsx from "clsx";
import PlannedOperation from "./features/PlannedOperation";
import LifeCycleAssessment from "./features/LifeCycleAssessment";
import AutomaticProductMapping from "./features/AutomaticProductMapping";
import { buildingStore } from "store/Building/BuildingStore";
import { placeDetailsToString } from "utils";
import {
  BuildingSettingsProps,
  buildingSettingsStore,
} from "./BuildingSettingsStore.ts";
import { observer } from "mobx-react-lite";
import { IconExclamationMark } from "@tabler/icons-react";
import MassCalculation from "./features/MassCalculation";
import IfcParserConfiguration from "./features/IfcParserConfiguration.tsx";
import Lifespan from "./features/Lifespan.tsx";

interface ActiveMenu {
  id: number;
  content: React.ReactNode;
}

const BuildingSettings = ({
  open,
  close,
  type,
}: {
  open: boolean;
  close: () => void;
  type: "add" | "edit";
}) => {
  const { t } = useTranslation();
  const { currentBuilding } = buildingStore;

  function loadBuilding() {
    if (!currentBuilding) return;
    const savedState = {
      ...currentBuilding,
      name: currentBuilding.name,
      upcoming_operation: currentBuilding?.upcoming_operation,
      address: {
        ...currentBuilding?.address,
        postal_code: currentBuilding?.postal_code,
        region: currentBuilding?.region,
      },
      string_address: currentBuilding?.address
        ? placeDetailsToString({
            ...currentBuilding.address,
            postal_code: currentBuilding?.postal_code ?? "",
            region: currentBuilding?.region ?? "",
          } as PlaceDetailsOUT)
        : "",
      building_type_id: currentBuilding.building_type?.id,
      use_imported_mass: currentBuilding.calculation_option?.use_imported_mass,
      calculation_mode: (
        currentBuilding as { calculation_mode?: BuildingCalculationEngine }
      ).calculation_mode,
      ...currentBuilding.certification_export_settings,
    };
    buildingSettingsStore.setSettings({
      ...savedState,
    } as BuildingSettingsProps);
  }

  React.useEffect(() => {
    type === "edit" && loadBuilding();
    if (buildingSettingsStore.openCertificationsTab) {
      setActiveMenu({
        id: menuList[1].items[2].id,
        content: menuList[1].items[2].content,
      });
    }
    return () => buildingSettingsStore.reset();
  }, []);

  const menuList = [
    {
      header: t("buildingSettings.general"),
      items: [
        {
          id: 1_1,
          name: t("buildingSettings.buildingInformation"),
          content: <BuildingInformation type={type} />,
          hasError: buildingSettingsStore.informationErrors,
        },
        {
          id: 1_2,
          name: t("buildingSettings.plannedOperation"),
          content: <PlannedOperation />,
        },
      ],
    },
    {
      header: t("buildingSettings.calculationLogic"),
      items: [
        {
          id: 2_1,
          name: t("buildingSettings.lifespan"),
          content: <Lifespan />,
        },
        {
          id: 2_2,
          name: t("buildingSettings.calculationRules"),
          content: <MassCalculation />,
        },
        {
          id: 2_3,
          name: t("buildingSettings.lifeCycleAssessmentForCertification"),
          content: <LifeCycleAssessment />,
          hasError: buildingSettingsStore.certificationErrors,
        },
      ],
    },
    {
      header: t("buildingSettings.import"),
      items: [
        {
          id: 3_1,
          name: t("buildingSettings.ifcParserConfigTitle"),
          content: <IfcParserConfiguration />,
        },
        {
          id: 3_2,
          name: t("buildingSettings.automaticProductMapping"),
          content: <AutomaticProductMapping />,
        },
      ],
    },
  ];

  const [activeMenu, setActiveMenu] = React.useState<ActiveMenu>({
    id: menuList[0].items[0].id,
    content: menuList[0].items[0].content,
  });

  const onChangeTab = (menu: ActiveMenu) => {
    setActiveMenu(menu);
    buildingSettingsStore.checkHasValidationsError();
  };

  return (
    <Modal
      setIsOpen={close}
      isOpen={open}
      title={
        type === "edit"
          ? t("buildingSettings.title")
          : t("buildingSettings.createNewBuilding")
      }
      className="flex overflow-y-auto"
      containerClassName={clsx("min-w-[1050px] bg-green-100", {
        "min-w-[640px]": type === "add",
      })}
      disableOutsideClick
      footer={<SettingsFooter close={close} type={type} />}
    >
      {type === "edit" && (
        <div className="min-w-[300px] border-r border-gray-300">
          {menuList.map((rec, index) => (
            <div key={index}>
              <div
                className={clsx(
                  "text-xs font-medium bg-gray-50 px-4 py-1 border-gray-300 border-y text-gray-500",
                  { "border-t-0": index === 0 }
                )}
              >
                {rec.header}
              </div>
              {rec.items.map((item, index) => (
                <div
                  key={index}
                  className={clsx(
                    "text-sm font-medium px-4 py-2 hover:bg-indigo-50 hover:text-indigo-700",
                    "cursor-pointer transition flex items-start break-words",
                    {
                      "bg-indigo-50 text-indigo-700": item.id == activeMenu.id,
                    }
                  )}
                  onClick={() =>
                    onChangeTab({ id: item.id, content: item.content })
                  }
                >
                  {item.hasError && (
                    <IconExclamationMark
                      className="min-w-[20px] w-[20px] h-[20px] mr-2 text-red-600 bg-red-100 rounded-full p-1"
                      stroke={3}
                    />
                  )}
                  {item.name}
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
      <div className="w-full flex flex-col py-4 px-6 overflow-y-auto h-[calc(100vh-200px)]">
        {activeMenu.content}
      </div>
    </Modal>
  );
};

export default observer(BuildingSettings);
